import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import StatusPill from "./StatusPill";
import { fmtDate } from "../lib/api";
import { ClipboardList, Edit3 } from "lucide-react";
import { toast } from "sonner";
import MobPopover from "./MobPopover";
import MobItemsEditDialog from "./MobItemsEditDialog";
import FrsDateCell from "./FrsDateCell";

// Compose a single "Location" string from a mob's primary FRS items. Format:
//   "Bldg A / F2 / AB"   when all 3 levels exist
//   "Bldg A / F2"        when no area
//   "Rooms ×8"           when nothing above room is set
function deriveLocation(m) {
  const parts = [];
  if (m.building) parts.push(`Bldg ${String(m.building).replace(/^Building\s+/i, "")}`);
  if (m.floor != null && m.floor !== "") parts.push(`F${m.floor}`);
  if (m.area) parts.push(m.area);
  if (parts.length === 0) {
    // Fall back to room count (the mob's total_rooms is computed server-side)
    const n = m.total_rooms || (m.frs_item_ids || []).length;
    return n ? `Rooms ×${n}` : "—";
  }
  return parts.join(" / ");
}

export default function MobilizationsList({ projectId }) {
  const [mobs, setMobs] = useState([]);
  const [installersMap, setInstallersMap] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [popoverMob, setPopoverMob] = useState(null);
  const [editItemsMob, setEditItemsMob] = useState(null);

  // Persist a date change inline. Auto-snaps end_date >= start_date.
  const patchDates = async (mob, field, value) => {
    const patch = { [field]: value };
    if (field === "scheduled_start_date" && value) {
      const cur = mob.scheduled_end_date || mob.requested_end_date;
      if (!cur || cur < value) patch.scheduled_end_date = value;
      if (mob.status === "requested") patch.status = "scheduled";
    }
    if (field === "scheduled_end_date" && value) {
      const cur = mob.scheduled_start_date || mob.requested_start_date;
      if (cur && value < cur) {
        toast.error("End date can't be before start date");
        return;
      }
    }
    try {
      const r = await api.patch(`/mobilizations/${mob.id}`, patch);
      setMobs((cur) => cur.map((m) => (m.id === mob.id ? { ...m, ...r.data } : m)));
      toast.success("Date saved");
    } catch {
      toast.error("Could not save date");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [mobsR, instR, projR] = await Promise.all([
        api.get("/mobilizations", { params: projectId ? { project_id: projectId } : {} }),
        api.get("/installers"),
        projectId ? api.get(`/projects/${projectId}`) : Promise.resolve({ data: null }),
      ]);
      setProject(projR.data || null);
      // Per spec: a mob that's been fully recalled (frs_item_ids empty AND not
      // currently scheduled or dispatched) drops off this list. Recall is
      // already reflected in the records via /api/frs-items/recall trimming
      // the parent mob's frs_item_ids.
      const visible = (mobsR.data || []).filter((m) => {
        const hasItems = (m.frs_item_ids || []).length > 0;
        const inFlight = !!(m.scheduled_start_date) || ["scheduled", "ready_to_dispatch", "dispatched"].includes(m.status);
        return hasItems || inFlight;
      });
      setMobs(visible);
      const im = {};
      (instR.data || []).forEach((i) => { im[i.id] = i; });
      setInstallersMap(im);
      // Fetch assignments for the visible mobs (one batched call). Build a
      // wide enough date window to cover all known mobs.
      const dates = visible
        .flatMap((m) => [m.scheduled_start_date, m.scheduled_end_date])
        .filter(Boolean)
        .sort();
      if (dates.length) {
        const aR = await api.get("/assignments", { params: { date_from: dates[0], date_to: dates[dates.length - 1] } });
        setAssignments(aR.data || []);
      } else {
        setAssignments([]);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  // Derive the unique installer names assigned to each mob.
  const installersForMob = useMemo(() => {
    const byMob = {};
    for (const a of assignments) {
      if (!a.mobilization_id) continue;
      (byMob[a.mobilization_id] ||= new Set()).add(a.installer_id);
    }
    const out = {};
    for (const [mid, ids] of Object.entries(byMob)) {
      out[mid] = [...ids].map((iid) => installersMap[iid]?.name).filter(Boolean);
    }
    return out;
  }, [assignments, installersMap]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-visible">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-[11px] font-semibold text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2 whitespace-nowrap">Title</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Location</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Scope</th>
            <th className="text-right px-3 py-2 whitespace-nowrap">Qty</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Installers</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Requested</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Start</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">End</th>
            <th className="text-left px-3 py-2 whitespace-nowrap">Status</th>
            <th className="text-center px-3 py-2 whitespace-nowrap">Edit Rooms</th>
            <th className="text-center px-3 py-2 whitespace-nowrap">Lineout</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={11} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
          ) : mobs.length === 0 ? (
            <tr><td colSpan={11} className="px-3 py-12 text-center text-gray-500">No mobilizations.</td></tr>
          ) : mobs.map((m) => {
            const insts = installersForMob[m.id] || [];
            const isLocked = ["dispatched", "completed", "cancelled"].includes(m.status);
            return (
              <tr key={m.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`mob-row-${m.id}`}>
                <td className="px-3 py-2 font-medium text-gray-900">{m.title}</td>
                <td className="px-3 py-2 text-gray-700 text-xs">{deriveLocation(m)}</td>
                <td className="px-3 py-2 text-gray-600 max-w-[300px] truncate" title={m.scope_type || m.scope_summary}>{m.scope_type || "—"}</td>
                <td className="px-3 py-2 text-right font-medium">{m.total_quantity}</td>
                <td className="px-3 py-2 text-xs text-gray-700 max-w-[260px]" title={insts.join(", ")}>
                  {insts.length === 0 ? <span className="text-gray-300">—</span> : (
                    <span className="truncate inline-block">{insts.length <= 2 ? insts.join(", ") : `${insts.slice(0, 2).join(", ")} +${insts.length - 2}`}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(m.requested_start_date)}</td>
                <td className="px-3 py-2 text-xs">
                  <FrsDateCell value={m.scheduled_start_date || ""} readOnly={isLocked} testId={`mob-start-date-${m.id}`}
                               hint={m.scheduled_start_date ? "Scheduled start" : `Not scheduled yet — FRS asks for ${fmtDate(m.requested_start_date) || "—"}`}
                               onCommit={(v) => { if (v !== (m.scheduled_start_date || "")) patchDates(m, "scheduled_start_date", v); }} />
                </td>
                <td className="px-3 py-2 text-xs">
                  <FrsDateCell value={m.scheduled_end_date || ""} readOnly={isLocked} testId={`mob-end-date-${m.id}`}
                               hint={m.scheduled_end_date ? "Scheduled end" : `Not scheduled yet — FRS asks for ${fmtDate(m.requested_end_date) || "—"}`}
                               onCommit={(v) => { if (v !== (m.scheduled_end_date || "")) patchDates(m, "scheduled_end_date", v); }} />
                </td>
                <td className="px-3 py-2"><StatusPill value={m.status} /></td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => setEditItemsMob(m)}
                    title="Edit / reorder rooms & scopes in this mob"
                    aria-label={`Edit rooms in ${m.title}`}
                    data-testid={`mob-edit-items-btn-${m.id}`}
                    disabled={isLocked}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => setPopoverMob(m)}
                    title="View Lineout for this mobilization"
                    aria-label={`View lineout for ${m.title}`}
                    data-testid={`mob-lineout-btn-${m.id}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 transition-colors"
                  >
                    <ClipboardList className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <MobPopover
        open={!!popoverMob}
        onOpenChange={(o) => { if (!o) setPopoverMob(null); }}
        mob={popoverMob}
        project={project}
        onChanged={(patch) => {
          if (!popoverMob) return;
          setMobs((cur) => cur.map((m) => m.id === popoverMob.id ? { ...m, ...patch } : m));
          setPopoverMob((cur) => cur ? { ...cur, ...patch } : cur);
        }}
      />
      <MobItemsEditDialog
        open={!!editItemsMob}
        onClose={() => setEditItemsMob(null)}
        mob={editItemsMob}
        onSaved={(updated) => {
          setEditItemsMob(null);
          if (updated) {
            setMobs((cur) => cur.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
          } else {
            load();
          }
        }}
      />
    </div>
  );
}
